import React from "react";
import {
  Alert,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import {
  canEditInvoice,
  formatCurrencyFromCents,
  formatDateForDisplay,
  getDaysPastDue,
  getInvoiceStatusLabel,
  getInvoiceSubtotalInCents,
  getInvoiceTaxInCents,
  getInvoiceTotalInCents,
  getInvoiceTypeLabel,
  isInvoiceOverdue,
  MockInvoice,
  MockInvoiceLineItem,
} from "./mockInvoiceData";
import { commonStyles } from "../../common/commonStyles";
import {
  centsToCurrencyValue,
  currencyValueToCents,
  sanitizeCurrencyValue,
} from "../../common/dashboard/currencyUnits";
import { SectionShell } from "../../common/dashboard/SectionShell";

// Re-exported for the existing consumer fan-out; canonical values live in
// commonStyles (POL-006 token consolidation).
export const cpaGold = commonStyles.cpaGold;
export const cpaGoldDark = commonStyles.cpaGoldDark;

const getRoutingModeLabel = (routingMode: MockInvoice["emailRouting"]["routingMode"]) => {
  switch (routingMode) {
    case "LIVE_RECIPIENTS":
      return "Live recipients";
    case "TEST_INBOX_OVERRIDE":
      return "Test inbox override";
    case "NOT_SENT":
      return "Not sent yet";
  }
};

export const InvoiceReadinessAlert = () => (
  <Alert severity="info" className="mb-5">
    Invoice emails, PDFs, CSV exports, voids, manual payments, manual refunds, and
    Stripe refund paths run through CAP backend safeguards. Email sends store intended
    and actual recipients separately, and live delivery requires explicit recipient
    routing configuration.
  </Alert>
);

// Card sections keep the shared platform skin. The applied invoice-create
// design uses plain headings inside one outer card, so the plain variant must
// not add another border or gold title pill.
export const InvoiceSection = ({
  children,
  label,
  title,
  description,
  variant = "card",
}: {
  children: React.ReactNode;
  className?: string;
  label: string;
  title: string;
  description?: string;
  variant?: "card" | "plain";
}) => {
  const sectionId = `invoice-section-${label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")}`;

  if (variant === "plain") {
    return (
      <section id={sectionId}>
        <div className="mb-[22px] flex flex-col gap-1">
          <h3 className="text-xl font-bold leading-[26px] text-black">
            {title}
          </h3>
          {description ? (
            <p className="max-w-5xl text-sm leading-[18px] text-black">
              {description}
            </p>
          ) : null}
        </div>
        {children}
      </section>
    );
  }

  return (
    <SectionShell
      id={sectionId}
      title={title}
      description={
        <>
          <span className="block text-xs font-bold uppercase tracking-wide text-gray-500">
            {label}
          </span>
          {description}
        </>
      }
    >
      {children}
    </SectionShell>
  );
};

// POL-005 (B5): shared editable money cell for the invoice line tables. Keeps
// the cents storage contract at the boundary (emits integer cents), kills the
// keystroke re-format jank with a focus/blur draft-string model, blurs to a
// canonical 2-decimal display, and always shows the "$" prefix. Text input
// with inputMode="decimal" — no number-spinner arrows.
export const InvoiceMoneyTextField = ({
  valueInCents,
  onValueInCentsChange,
  disabled = false,
  error = false,
  helperText,
  placeholder,
  sx,
  ariaLabel,
}: {
  valueInCents: number;
  onValueInCentsChange: (valueInCents: number) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  sx?: object;
  ariaLabel?: string;
}) => {
  const [draftValue, setDraftValue] = React.useState<string | null>(null);

  return (
    <TextField
      size="small"
      fullWidth
      value={draftValue !== null ? draftValue : centsToCurrencyValue(valueInCents)}
      disabled={disabled}
      error={error}
      helperText={helperText}
      placeholder={placeholder}
      sx={sx}
      inputProps={{ inputMode: "decimal", "aria-label": ariaLabel }}
      InputProps={{
        startAdornment: <span className="mr-1 text-gray-500">$</span>,
      }}
      onFocus={() =>
        setDraftValue(
          valueInCents ? sanitizeCurrencyValue((valueInCents / 100).toString()) : ""
        )
      }
      onChange={({ target }) => {
        const sanitized = sanitizeCurrencyValue(target.value);
        setDraftValue(sanitized);
        onValueInCentsChange(currencyValueToCents(sanitized));
      }}
      onBlur={() => setDraftValue(null)}
    />
  );
};

export const InvoiceStatusChip = ({ invoice }: { invoice: MockInvoice }) => {
  const className = (() => {
    if (isInvoiceOverdue(invoice)) {
      return "bg-red-100 text-red-800";
    }

    switch (invoice.status) {
      case "draft":
        return "bg-gray-100 text-gray-600";
      case "paid":
        return "bg-green-100 text-green-800";
      case "unpaid":
        return "bg-amber-100 text-amber-800";
      case "void":
        return "bg-gray-100 text-gray-700";
      case "waived":
        return "bg-sky-100 text-sky-800";
      case "refunded":
        return "bg-blue-100 text-blue-800";
      case "failed":
        return "bg-red-100 text-red-800";
      case "abandoned":
      case "expired":
        return "bg-orange-100 text-orange-800";
      case "actionRequired":
        return "bg-purple-100 text-purple-800";
    }
  })();

  return (
    <Chip
      label={getInvoiceStatusLabel(invoice)}
      size="small"
      className={`${className} font-semibold`}
    />
  );
};

export const InvoiceTypeChip = ({ invoice }: { invoice: MockInvoice }) => (
  <Chip
    label={getInvoiceTypeLabel(invoice.type)}
    size="small"
    variant="outlined"
    className="border-gray-300 text-gray-700"
  />
);

export const InvoiceEmailRoutingPanel = ({
  invoice,
  title = "Email Routing",
}: {
  invoice: MockInvoice;
  title?: string;
}) => (
  <div className="rounded border border-blue-100 bg-blue-50 p-4">
    <div className="mb-3 flex items-center gap-2 text-sm font-bold text-blue-900">
      <EmailOutlinedIcon fontSize="small" />
      {title}
    </div>
    <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
      <div>
        <dt className="font-semibold text-blue-900">Intended recipient</dt>
        <dd className="mt-1 text-blue-800">{invoice.emailRouting.intendedRecipientEmail}</dd>
      </div>
      <div>
        <dt className="font-semibold text-blue-900">Actual recipient</dt>
        <dd className="mt-1 text-blue-800">
          {invoice.emailRouting.actualRecipientEmail || "Set when sent"}
          {invoice.emailRouting.recipientOverrideUsed ? " (test override)" : ""}
        </dd>
      </div>
      <div>
        <dt className="font-semibold text-blue-900">Routing mode</dt>
        <dd className="mt-1 text-blue-800">
          {getRoutingModeLabel(invoice.emailRouting.routingMode)}
        </dd>
      </div>
      <div>
        <dt className="font-semibold text-blue-900">Email state</dt>
        <dd className="mt-1 text-blue-800">{invoice.emailRouting.state}</dd>
      </div>
    </dl>
  </div>
);

export const InvoiceTotals = ({ invoice }: { invoice: Pick<MockInvoice, "lineItems"> }) => {
  const subtotal = getInvoiceSubtotalInCents(invoice);
  const tax = getInvoiceTaxInCents(invoice);
  const total = getInvoiceTotalInCents(invoice);

  return (
    <div className="ml-auto w-full max-w-sm space-y-2 text-sm">
      <div className="flex justify-between">
        <span className="text-gray-600">Subtotal</span>
        <span className="font-medium text-gray-950">{formatCurrencyFromCents(subtotal)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-600">Tax</span>
        <span className="font-medium text-gray-950">{formatCurrencyFromCents(tax)}</span>
      </div>
      <Divider />
      <div className="flex justify-between text-base">
        <span className="font-bold text-gray-950">Total</span>
        <span className="font-bold text-gray-950">{formatCurrencyFromCents(total)}</span>
      </div>
    </div>
  );
};

export const InvoicePreview = ({ invoice }: { invoice: MockInvoice }) => (
  <Paper elevation={0} className="rounded border border-gray-200 bg-white p-5">
    <div className="flex flex-col justify-between gap-5 md:flex-row">
      <div>
        <p className="text-lg font-bold text-gray-950">Sand Bay Chamber of Commerce</p>
        <p className="mt-1 text-sm text-gray-600">123 Main Street, Sand Bay, AB</p>
        <p className="text-sm text-gray-600">admin@chamberxyz.ca</p>
      </div>
      <div className="text-left md:text-right">
        <p className="text-3xl font-bold uppercase text-gray-200">Invoice</p>
        <p className="mt-1 text-lg font-bold text-gray-950">#{invoice.invoiceNumber}</p>
        <div className="mt-2">
          <InvoiceStatusChip invoice={invoice} />
        </div>
      </div>
    </div>

    <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
      <div>
        <p className="text-xs font-bold uppercase text-gray-500">Bill To</p>
        <p className="mt-2 font-semibold text-gray-950">{invoice.billingContactName}</p>
        <p className="text-sm text-gray-700">{invoice.customerName}</p>
        <p className="text-sm text-gray-600">{invoice.billingAddress}</p>
        <p className="text-sm text-gray-600">{invoice.billingEmail}</p>
      </div>
      <div>
        <p className="text-xs font-bold uppercase text-gray-500">Invoice Details</p>
        <dl className="mt-2 space-y-1 text-sm">
          <div className="flex justify-between gap-3">
            <dt className="text-gray-500">Issued</dt>
            <dd className="font-medium text-gray-950">
              {formatDateForDisplay(invoice.issueDate)}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-gray-500">Due</dt>
            <dd className="font-medium text-gray-950">
              {formatDateForDisplay(invoice.dueDate)}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-gray-500">Type</dt>
            <dd className="font-medium text-gray-950">
              {getInvoiceTypeLabel(invoice.type)}
            </dd>
          </div>
        </dl>
      </div>
      <div>
        <p className="text-xs font-bold uppercase text-gray-500">Payment</p>
        <dl className="mt-2 space-y-1 text-sm">
          <div className="flex justify-between gap-3">
            <dt className="text-gray-500">Requested</dt>
            <dd className="font-medium text-gray-950">
              {invoice.requestedPaymentMethodLabel}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-gray-500">Actual</dt>
            <dd className="font-medium text-gray-950">{invoice.paymentMethodLabel}</dd>
          </div>
          {invoice.paidDate ? (
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">Paid</dt>
              <dd className="font-medium text-gray-950">
                {formatDateForDisplay(invoice.paidDate)}
              </dd>
            </div>
          ) : null}
        </dl>
      </div>
    </div>

    <TableContainer className="mt-6 rounded border border-gray-200">
      <Table size="small" aria-label="invoice preview line items">
        <TableHead className="bg-gray-50">
          <TableRow>
            <TableCell className="font-bold">Description</TableCell>
            <TableCell align="right" className="font-bold">
              Qty
            </TableCell>
            <TableCell align="right" className="font-bold">
              Unit Price
            </TableCell>
            <TableCell align="right" className="font-bold">
              Tax
            </TableCell>
            <TableCell align="right" className="font-bold">
              Amount
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoice.lineItems.map((item) => (
            <TableRow key={item.id}>
              <TableCell>{item.description}</TableCell>
              <TableCell align="right">{item.quantity}</TableCell>
              <TableCell align="right">
                {formatCurrencyFromCents(item.unitPriceInCents)}
              </TableCell>
              <TableCell align="right">{item.taxRate}%</TableCell>
              <TableCell align="right">
                {formatCurrencyFromCents(item.quantity * item.unitPriceInCents)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

    <div className="mt-6">
      <InvoiceTotals invoice={invoice} />
    </div>

    {invoice.publicMemo || invoice.notes ? (
      <div className="mt-6 rounded bg-gray-50 p-4 text-sm text-gray-700">
        <p className="font-semibold text-gray-950">Memo</p>
        <p className="mt-1">{invoice.publicMemo || invoice.notes}</p>
      </div>
    ) : null}
  </Paper>
);

const formatTaxRatePercentage = (value: number) =>
  String(Math.round(value * 100) / 100);

export const InvoiceLineItemsEditor = ({
  lineItems,
  onChange,
  readOnly = false,
  descriptionErrors,
}: {
  lineItems: MockInvoiceLineItem[];
  onChange: (lineItems: MockInvoiceLineItem[]) => void;
  readOnly?: boolean;
  /** POL-019 (B5): per-line description error messages keyed by line item id. */
  descriptionErrors?: Record<string, string>;
}) => {
  const handleChange = (
    itemId: string,
    field: keyof MockInvoiceLineItem,
    value: string
  ) => {
    onChange(
      lineItems.map((item) => {
        if (item.id !== itemId) {
          return item;
        }

        if (field === "quantity") {
          return { ...item, quantity: Math.max(1, Number(value) || 1) };
        }

        if (field === "unitPriceInCents") {
          return {
            ...item,
            unitPriceInCents: Math.max(0, Math.round((Number(value) || 0) * 100)),
          };
        }

        return { ...item, [field]: value };
      })
    );
  };

  const handleAddLineItem = () => {
    onChange([
      ...lineItems,
      {
        id: `line-${Date.now()}`,
        description: "New invoice item",
        quantity: 1,
        unitPriceInCents: 0,
        // A new line stays on the draft's saved tax snapshot. The editor does
        // not recalculate existing drafts from current Chamber Setup values.
        taxRate: lineItems[0]?.taxRate ?? 0,
      },
    ]);
  };

  const handleRemoveLineItem = (itemId: string) => {
    if (lineItems.length === 1) {
      return;
    }

    onChange(lineItems.filter((item) => item.id !== itemId));
  };

  return (
    <div className="space-y-4">
      <TableContainer component={Paper} elevation={0} className="rounded border">
        <Table size="small" aria-label="invoice line item editor">
          <TableHead className="bg-gray-50">
            <TableRow>
              <TableCell className="font-bold">Description</TableCell>
              <TableCell className="w-24 font-bold">Qty</TableCell>
              <TableCell className="w-36 font-bold">Unit Price</TableCell>
              <TableCell className="w-28 font-bold">Tax %</TableCell>
              <TableCell align="right" className="w-32 font-bold">
                Amount
              </TableCell>
              {!readOnly ? <TableCell className="w-12" /> : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {lineItems.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={item.description}
                    disabled={readOnly}
                    error={Boolean(descriptionErrors?.[item.id])}
                    helperText={descriptionErrors?.[item.id]}
                    onChange={({ target }) =>
                      handleChange(item.id, "description", target.value)
                    }
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={item.quantity}
                    disabled={readOnly}
                    inputProps={{ min: 1 }}
                    onChange={({ target }) =>
                      handleChange(item.id, "quantity", target.value)
                    }
                  />
                </TableCell>
                <TableCell>
                  {/* POL-005 (B5): $-prefixed decimal text input with a
                      focus/blur draft model; storage stays integer cents. */}
                  <InvoiceMoneyTextField
                    valueInCents={item.unitPriceInCents}
                    disabled={readOnly}
                    ariaLabel="Unit price"
                    onValueInCentsChange={(valueInCents) =>
                      onChange(
                        lineItems.map((candidate) =>
                          candidate.id === item.id
                            ? { ...candidate, unitPriceInCents: valueInCents }
                            : candidate
                        )
                      )
                    }
                  />
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-700">
                    {formatTaxRatePercentage(item.taxRate)}%
                  </span>
                </TableCell>
                <TableCell align="right" className="font-semibold">
                  {formatCurrencyFromCents(item.quantity * item.unitPriceInCents)}
                </TableCell>
                {!readOnly ? (
                  <TableCell>
                    <Tooltip title="Remove line item">
                      <span>
                        <IconButton
                          size="small"
                          disabled={lineItems.length === 1}
                          onClick={() => handleRemoveLineItem(item.id)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!readOnly ? (
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddLineItem}
          className="border-gray-300 text-gray-700"
        >
          Add Line Item
        </Button>
      ) : null}
    </div>
  );
};

export const InvoiceHistory = ({ invoice }: { invoice: MockInvoice }) => (
  <div className="space-y-3">
    {invoice.history.map((event) => (
      <div
        key={event.id}
        className="rounded border border-gray-200 bg-gray-50 p-3 text-sm"
      >
        <div className="flex flex-col justify-between gap-1 sm:flex-row">
          <span className="font-semibold text-gray-950">{event.label}</span>
          <span className="text-gray-500">{formatDateForDisplay(event.occurredAt)}</span>
        </div>
        <p className="mt-1 text-gray-700">{event.detail}</p>
        <p className="mt-1 text-xs font-semibold uppercase text-gray-500">
          {event.actor}
        </p>
      </div>
    ))}
  </div>
);

export const InvoiceStateNotice = ({ invoice }: { invoice: MockInvoice }) => {
  if (canEditInvoice(invoice)) {
    return (
      <Alert severity="warning" className="mb-5">
        Updating this unpaid invoice uses the accounting-safe flow: void the
        original invoice and create a replacement invoice. The original record
        remains in history.
      </Alert>
    );
  }

  if (invoice.status === "paid") {
    return (
      <Alert severity="success" className="mb-5">
        This paid invoice is read-only. Staff can view, export, or start an allowed
        full-refund action, but the invoice itself is not editable.
      </Alert>
    );
  }

  return (
    <Alert severity="info" className="mb-5">
      This invoice is read-only because it is {getInvoiceStatusLabel(invoice)}.
      Available actions are limited by the invoice state.
    </Alert>
  );
};

export const InvoicePaymentMethodSelect = ({
  value,
  onChange,
  disabled = false,
  creditCardPaymentsReady = false,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  creditCardPaymentsReady?: boolean;
}) => (
  <TextField
    select
    fullWidth
    label="Requested Payment Method"
    value={value}
    disabled={disabled}
    onChange={({ target }) => onChange(target.value)}
  >
    <MenuItem value="Invoice Me">Invoice Me</MenuItem>
    {creditCardPaymentsReady ? (
      <MenuItem value="Credit card checkout">Credit card checkout</MenuItem>
    ) : null}
    <MenuItem value="Waived / no payment required">Waived / no payment required</MenuItem>
    <MenuItem value="Bank transfer">Bank transfer</MenuItem>
    <MenuItem value="E-Transfer">E-Transfer</MenuItem>
    <MenuItem value="Cheque">Cheque</MenuItem>
    <MenuItem value="Cash">Cash</MenuItem>
    <MenuItem value="Third-party payer">Third-party payer</MenuItem>
  </TextField>
);

export const OverdueHelp = () => (
  <div className="flex items-start gap-2 rounded border border-amber-100 bg-amber-50 p-3 text-sm text-amber-900">
    <InfoOutlinedIcon fontSize="small" className="mt-0.5 shrink-0" />
    <p>
      Overdue is derived from unpaid invoices with a past due date. No backend
      status is named overdue.
    </p>
  </div>
);

export const InvoiceSummaryCard = ({
  icon,
  label,
  value,
  helper,
}: {
  icon?: React.ReactNode;
  label: string;
  value: string;
  helper?: string;
}) => (
  <Paper elevation={0} className="rounded border border-gray-200 p-4">
    <div className="flex items-center gap-3">
      {icon ? (
        <div className="flex h-9 w-9 items-center justify-center rounded bg-gray-100 text-gray-700">
          {icon}
        </div>
      ) : (
        <ReceiptLongOutlinedIcon className="text-gray-500" />
      )}
      <div>
        <p className="text-xs font-bold uppercase text-gray-500">{label}</p>
        <p className="text-xl font-bold text-gray-950">{value}</p>
      </div>
    </div>
    {helper ? <p className="mt-2 text-xs text-gray-500">{helper}</p> : null}
  </Paper>
);

export const getOverdueDescription = (invoice: MockInvoice) => {
  const daysPastDue = getDaysPastDue(invoice);

  if (!daysPastDue) {
    return "Current";
  }

  if (daysPastDue >= 120) {
    return "120+ days past due";
  }

  if (daysPastDue >= 90) {
    return "90-119 days past due";
  }

  if (daysPastDue >= 60) {
    return "60-89 days past due";
  }

  return `${daysPastDue} days past due`;
};
